import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

const money = value => new Intl.NumberFormat('en-LK', { style: 'currency', currency: 'LKR' }).format(Number(value || 0));
const today = () => new Date().toLocaleDateString('en-CA');
const requestId = () => globalThis.crypto?.randomUUID?.() || `cod-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const selectClass = 'h-10 rounded-md border border-slate-200 bg-white px-3 text-sm';
const message = error => typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Could not complete this request. Check the fields and retry.';
const emptyReport = { settlements: [], fees: [], totals: {} };
const csvCell = value => '"' + String(value ?? '').replace(/^[=+@-]/, "'$&").replaceAll('"', '""') + '"';

export default function CODSettlements() {
  const [tab, setTab] = useState('orders');
  const [status, setStatus] = useState('awaiting');
  const [courier, setCourier] = useState('');
  const [search, setSearch] = useState('');
  const [dates, setDates] = useState({ start_date: '', end_date: '' });
  const [rows, setRows] = useState([]);
  const [report, setReport] = useState(emptyReport);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const loadVersion = useRef(0);

  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true); setLoadError('');
    try {
      const [orders, banks, history] = await Promise.all([
        api.get('/cod-settlements', { params: { status, courier: courier || undefined } }),
        api.get('/bank-accounts'),
        api.get('/cod-settlement-report', { params: { ...dates, courier: courier || undefined } }),
      ]);
      if (version !== loadVersion.current) return;
      setRows(orders.data || []);
      setAccounts((banks.data || []).filter(account => account.is_active !== false && ['bank', 'cash'].includes(account.account_type)));
      setReport(history.data || emptyReport);
    } catch (error) {
      if (version === loadVersion.current) setLoadError(message(error));
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  }, [status, courier, dates]);

  useEffect(() => { load(); }, [load]);
  const visible = useMemo(() => rows.filter(row =>
    [row.order_number, row.customer_name, row.courier_partner].join(' ').toLowerCase().includes(search.toLowerCase())
  ), [rows, search]);
  const outstanding = visible.filter(row => row.delivery_status === 'delivered' && !row.stock_restored_at)
    .reduce((sum, row) => sum + Number(row.settlement_balance ?? 0), 0);

  const open = (row, kind) => {
    setSelected({ ...row, kind });
    setForm({
      amount: kind === 'fee' ? '' : String(row.settlement_balance ?? 0),
      fee_amount: '0', bank_account_id: '', payment_date: today(),
      settlement_reference: '', notes: '', client_request_id: requestId(),
    });
  };
  const gross = Number(form.amount || 0);
  const deduction = selected?.kind === 'fee' ? 0 : Number(form.fee_amount || 0);
  const net = gross - deduction;
  const valid = gross > 0 && deduction >= 0 && net >= 0 && form.payment_date &&
    (net === 0 || form.bank_account_id) &&
    Boolean(form.settlement_reference?.trim() || form.notes?.trim()) &&
    (selected?.kind === 'fee' || gross <= Number(selected?.settlement_balance ?? 0));

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      const body = { ...form, amount: gross, fee_amount: deduction, bank_account_id: form.bank_account_id || null };
      if (selected.kind === 'fee') await api.post('/cod-courier-fees', { ...body, order_id: selected.id });
      else await api.post('/cod-settlements/' + selected.id, body);
      toast.success(selected.kind === 'fee' ? 'Courier fee recorded' : 'Settlement recorded');
      setSelected(null);
      await load();
    } catch (error) {
      toast.error(message(error));
    } finally {
      setSaving(false);
    }
  };

  const exportReport = () => {
    const lines = [['Type', 'Date', 'Order', 'Courier', 'Reference', 'Gross', 'Fee', 'Net received']];
    report.settlements.forEach(row => lines.push(['Settlement', row.payment_date, row.reference_number, row.courier_name,
      row.settlement_reference, row.amount, row.fee_amount || 0, row.net_amount ?? row.amount]));
    report.fees.filter(row => row.kind === 'separately_paid').forEach(row =>
      lines.push(['Separate fee', row.payment_date, row.reference_number, row.courier_name,
        row.settlement_reference, 0, row.amount, -row.amount]));
    const url = URL.createObjectURL(new Blob([
      '\ufeff' + lines.map(line => line.map(csvCell).join(',')).join('\r\n')
    ], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url; link.download = 'cod-courier-settlement-report.csv'; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return <div className="space-y-5" data-testid="cod-settlements-page">
    <div>
      <h2 className="text-2xl font-bold">COD Settlements</h2>
      <p className="text-slate-500">Receivables are held separately for Sri Lanka Post and Trans Express. Record only money and deductions confirmed by a courier statement.</p>
    </div>
    <div className="flex flex-wrap gap-2">
      {['orders', 'reports', 'fees'].map(value =>
        <Button key={value} variant={tab === value ? 'default' : 'outline'} onClick={() => setTab(value)}>
          {{ orders: 'COD orders', reports: 'Settlement reports', fees: 'Courier fees' }[value]}
        </Button>)}
    </div>
    <div className="flex flex-wrap gap-3 items-end">
      <div><Label htmlFor="cod-courier">Courier partner</Label>
        <select id="cod-courier" className={selectClass + ' block'} value={courier} onChange={event => setCourier(event.target.value)}>
          <option value="">All couriers</option><option value="sri-lanka-post">Sri Lanka Post</option><option value="trans-express">Trans Express</option>
        </select>
      </div>
      {tab === 'orders' ? <>
        <div><Label htmlFor="cod-status">Order status</Label>
          <select id="cod-status" className={selectClass + ' block'} value={status} onChange={event => setStatus(event.target.value)}>
            <option value="awaiting">Awaiting / partial settlement</option><option value="settled">Settled</option>
            <option value="returned">Returned</option><option value="all">All COD orders</option>
          </select>
        </div>
        <Input className="max-w-xs" aria-label="Search COD orders" placeholder="Search order or customer" value={search} onChange={event => setSearch(event.target.value)} />
      </> : <>
        <div><Label>From (posting date)</Label><Input type="date" value={dates.start_date} onChange={event => setDates({ ...dates, start_date: event.target.value })} /></div>
        <div><Label>To (posting date)</Label><Input type="date" value={dates.end_date} onChange={event => setDates({ ...dates, end_date: event.target.value })} /></div>
        <Button variant="outline" disabled={loading || Boolean(loadError)} onClick={exportReport}>Download CSV</Button>
      </>}
      <Button variant="outline" onClick={load} disabled={loading}>Refresh</Button>
    </div>
    {loadError && <p role="alert" className="text-red-600">{loadError}</p>}
    {loading ? <p>Loading COD records...</p> : !loadError && <>
      {tab === 'orders' ? <>
        <Card><CardContent className="p-4"><p>Outstanding for delivered orders in this view</p><strong>{money(outstanding)}</strong></CardContent></Card>
        <div className="overflow-x-auto rounded border"><table className="w-full text-sm"><thead><tr className="bg-slate-100 text-left">
          {['Order / customer', 'Courier / account', 'Delivered', 'Total', 'Settled', 'Outstanding', 'Status', 'Actions'].map(heading => <th className="p-3" key={heading}>{heading}</th>)}
        </tr></thead><tbody>{visible.map(row => <tr className="border-t" key={row.id}>
          <td className="p-3">{row.order_number}<div className="text-slate-500">{row.customer_name}</div></td>
          <td className="p-3">{row.courier_partner}<div>{row.courier_receivable_account_code}</div></td>
          <td className="p-3">{row.delivered_at?.slice(0, 10) || '—'}</td><td className="p-3">{money(row.total)}</td>
          <td className="p-3">{money(row.paid_amount)}</td><td className="p-3">{row.stock_restored_at ? 'Returned' : money(row.settlement_balance)}</td>
          <td className="p-3">{row.stock_restored_at ? 'returned' : row.cod_settlement_status || row.payment_status}</td>
          <td className="p-3 space-y-2">
            {row.delivery_status === 'delivered' && Number(row.settlement_balance) > 0 && <Button size="sm" onClick={() => open(row, 'settlement')}>Record settlement</Button>}
            <Button size="sm" variant="outline" onClick={() => open(row, 'fee')}>Record separate fee</Button>
          </td>
        </tr>)}</tbody></table>{!visible.length && <p className="p-5">No orders match these filters.</p>}</div>
      </> : <>
        <div className="grid gap-3 sm:grid-cols-4">{[
          ['Gross settled', report.totals.gross], ['Deducted fees', report.totals.deducted_fees],
          ['Bank/cash received', report.totals.net_received], ['Separately paid fees', report.totals.separately_paid_fees],
        ].map(([label, value]) => <Card key={label}><CardContent className="p-4"><p className="text-sm">{label}</p><strong>{money(value)}</strong></CardContent></Card>)}</div>
        {tab === 'fees' && <p>Deducted fees are recorded with settlement. For a return fee paid separately, open COD orders, choose All COD orders, and use Record separate fee. Do not enter the same charge twice.</p>}
        <div className="overflow-x-auto rounded border"><table className="w-full text-sm"><thead><tr className="text-left bg-slate-100">
          {['Date', 'Order', 'Courier', 'Reference', 'Amount', 'Fee / type', 'Net received'].map(heading => <th className="p-3" key={heading}>{heading}</th>)}
        </tr></thead><tbody>{(tab === 'fees' ? report.fees : report.settlements).map(row => <tr className="border-t" key={row.id}>
          <td className="p-3">{row.payment_date}</td><td className="p-3">{row.reference_number}</td><td className="p-3">{row.courier_name}</td>
          <td className="p-3">{row.settlement_reference || '—'}</td><td className="p-3">{money(row.amount)}</td>
          <td className="p-3">{tab === 'fees' ? row.kind : money(row.fee_amount)}</td><td className="p-3">{tab === 'fees' ? '—' : money(row.net_amount ?? row.amount)}</td>
        </tr>)}</tbody></table></div>
      </>}
    </>}
    <Dialog open={Boolean(selected)} onOpenChange={isOpen => !isOpen && !saving && setSelected(null)}><DialogContent>
      <DialogHeader><DialogTitle>{selected?.kind === 'fee' ? 'Record courier fee paid separately' : 'Record full or partial COD settlement'}</DialogTitle>
        <DialogDescription>{selected?.order_number} · {selected?.courier_partner}. This changes financial records.</DialogDescription></DialogHeader>
      <div className="space-y-3">
        <Label htmlFor="cod-amount">{selected?.kind === 'fee' ? 'Fee paid from bank/cash (LKR)' : 'Gross amount cleared by courier (LKR)'}</Label>
        <Input id="cod-amount" type="number" min="0.01" step="0.01" value={form.amount || ''} onChange={event => setForm({ ...form, amount: event.target.value })} />
        {selected?.kind !== 'fee' && <>
          <Label htmlFor="cod-fee">Courier fee deducted from this settlement (LKR)</Label>
          <Input id="cod-fee" type="number" min="0" step="0.01" value={form.fee_amount || '0'} onChange={event => setForm({ ...form, fee_amount: event.target.value })} />
          <p>Net to bank/cash: <strong>{money(net)}</strong>. Leave fee at zero if already recorded elsewhere.</p>
        </>}
        <Label htmlFor="cod-bank">{selected?.kind === 'fee' ? 'Paid from' : 'Received into'}</Label>
        <select id="cod-bank" className={selectClass + ' w-full'} value={form.bank_account_id || ''} onChange={event => setForm({ ...form, bank_account_id: event.target.value })}>
          <option value="">Select bank or cash account</option>{accounts.map(account => <option key={account.id} value={account.id}>{account.account_name || account.name} ({account.account_type})</option>)}
        </select>
        <Label htmlFor="cod-date">Posting date</Label><Input id="cod-date" type="date" value={form.payment_date || ''} onChange={event => setForm({ ...form, payment_date: event.target.value })} />
        <Label htmlFor="cod-reference">Courier statement / payment reference</Label><Input id="cod-reference" maxLength={120} value={form.settlement_reference || ''} onChange={event => setForm({ ...form, settlement_reference: event.target.value })} />
        <Label htmlFor="cod-notes">Notes / reason</Label><Input id="cod-notes" value={form.notes || ''} onChange={event => setForm({ ...form, notes: event.target.value })} />
      </div>
      <DialogFooter><Button variant="outline" disabled={saving} onClick={() => setSelected(null)}>Cancel</Button><Button disabled={saving || !valid} onClick={submit}>{saving ? 'Saving...' : 'Confirm posting'}</Button></DialogFooter>
    </DialogContent></Dialog>
  </div>;
}
